/* hlx-reviews.js — HorizonLux Google Reviews slider init
   - no dependencies
   - multi-instance safe
   - theme editor safe (section load/reorder)
*/
(() => {
  const DESKTOP_MQ = '(min-width: 750px)';
  const EAGER_CLAMP_RADIUS = 1;

  const raf2 = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));

  const isDesktop = () => window.matchMedia(DESKTOP_MQ).matches;

  function parseDDMMYY(s) {
    if (!s) return null;
    const str = String(s).trim();
    const m = str.match(/^(\d{1,2})-(\d{1,2})-(\d{2}|\d{4})$/);
    if (!m) return null;

    const d = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    let y = parseInt(m[3], 10);
    if (y < 100) y = (y >= 70) ? (1900 + y) : (2000 + y);

    const dt = new Date(y, mo - 1, d);
    if (Number.isNaN(dt.getTime())) return null;
    if (dt.getFullYear() !== y || dt.getMonth() !== (mo - 1) || dt.getDate() !== d) return null;
    return dt;
  }

  function pluralEn(n, unit) {
    return n === 1 ? `${n} ${unit}` : `${n} ${unit}s`;
  }

  function relativeEN(fromDate) {
    const now = new Date();
    const start = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const days = Math.max(0, Math.floor((end - start) / 86400000));

    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 7) return `${pluralEn(days, 'day')} ago`;

    if (days < 30) {
      const w = Math.floor(days / 7);
      return `${pluralEn(w, 'week')} ago`;
    }
    if (days < 365) {
      const m = Math.floor(days / 30);
      return `${pluralEn(m, 'month')} ago`;
    }
    const y = Math.floor(days / 365);
    return `${pluralEn(y, 'year')} ago`;
  }

  function initReviews(root) {
    if (!root || root.dataset.grvInit === '1') return;

    const viewport = root.querySelector('[data-grv-viewport]');
    const track = root.querySelector('[data-grv-track]');
    const slides = Array.from(root.querySelectorAll('[data-grv-slide]'));
    const prev = root.querySelector('[data-grv-prev]');
    const next = root.querySelector('[data-grv-next]');
    const dotsWrap = root.querySelector('[data-grv-dots]');

    if (!viewport || !track || slides.length === 0) return;

    root.dataset.grvInit = '1';

    const getInitialIndex = () => (isDesktop() && slides.length >= 3 ? 1 : 0);

    let index = getInitialIndex();
    let dots = [];
    let rebuildTimer = null;
    let ro = null;
    let clampTimer = null;
    let viewportWidth = 0;
    let slideCenters = [];

    function applyLayout() {
      track.style.display = 'flex';
      track.style.gap = '0px';
      track.style.overflowX = 'auto';
      track.style.overflowY = 'visible';
      track.style.scrollBehavior = 'smooth';
      track.style.scrollSnapType = 'x mandatory';
      track.style.webkitOverflowScrolling = 'touch';

      const per = isDesktop() ? 3 : 1;

      slides.forEach((s) => {
        s.style.display = 'block';
        s.style.flex = '0 0 auto';
        s.style.scrollSnapAlign = 'center';
        s.style.width = per === 1 ? '100%' : (100 / per) + '%';
      });
    }

    function setActive() {
      slides.forEach((el, i) => el.classList.toggle('is-active', i === index));
      dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
    }

    function refreshSlideMetrics() {
      viewportWidth = viewport.clientWidth;
      slideCenters = slides.map((s) => s.offsetLeft + s.clientWidth / 2);
    }

    function scrollTo(i, behavior = 'smooth') {
      const el = slides[i];
      if (!el) return;
      if (!slideCenters.length) refreshSlideMetrics();
      const left = slideCenters[i] - viewportWidth / 2;
      track.scrollTo({ left, behavior });
    }

    function renderDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      dots = [];

      for (let i = 0; i < slides.length; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'grv__dot';
        b.setAttribute('aria-label', 'Go to review ' + (i + 1));
        b.addEventListener('click', () => go(i));
        dotsWrap.appendChild(b);
        dots.push(b);
      }
      setActive();
    }

    function applyRelativeTimes() {
      root.querySelectorAll('[data-grv-date]').forEach((el) => {
        const raw = el.getAttribute('data-grv-date');
        const dt = parseDDMMYY(raw);
        if (!dt) return;
        el.textContent = relativeEN(dt);
        el.setAttribute('title', raw);
      });
    }

    // ===== Collapse / Expand =====
    function scopeAllowsCollapse() {
      const collapseOn = root.getAttribute('data-collapse') === 'true';
      if (!collapseOn) return false;

      const scope = (root.getAttribute('data-collapse-scope') || 'both').toLowerCase(); // both|mobile|desktop
      const desktopNow = isDesktop();

      return (
        scope === 'both' ||
        (scope === 'mobile' && !desktopNow) ||
        (scope === 'desktop' && desktopNow)
      );
    }

    function isActuallyClamped(textEl) {
      return (textEl.scrollHeight - textEl.clientHeight) > 2;
    }

    function shouldCheckClamp(slideIndex) {
      if (Math.abs(slideIndex - index) <= EAGER_CLAMP_RADIUS) return true;
      if (index === 0 && slideIndex === slides.length - 1) return true;
      if (index === slides.length - 1 && slideIndex === 0) return true;
      return false;
    }

    function setToggleForOverflow(slide, text, btn, hasOverflow) {
      if (hasOverflow) {
        btn.hidden = false;
      } else {
        text.classList.remove('is-collapsed');
        btn.hidden = true;
      }

      btn.dataset.clampChecked = '1';
    }

    function ensureCollapsedButton(text, btn) {
      if (!text.classList.contains('is-collapsed')) text.classList.add('is-collapsed');
      if (text.classList.contains('is-expanded')) text.classList.remove('is-expanded');

      const moreLabel = btn.dataset.moreLabel || 'Read more';
      if (btn.textContent !== moreLabel) btn.textContent = moreLabel;
      if (btn.getAttribute('aria-expanded') !== 'false') btn.setAttribute('aria-expanded', 'false');
    }

    function scheduleClampRefresh(delay = 120) {
      if (clampTimer) clearTimeout(clampTimer);
      clampTimer = setTimeout(() => raf2(recalcClampVisibilityOnly), delay);
    }

    function setCollapsedForSlide(slide, forceCollapsed) {
      const text = slide.querySelector('[data-grv-text]');
      const btn = slide.querySelector('[data-grv-toggle]');
      if (!text || !btn) return;

      const lessLabel = btn.dataset.lessLabel || (root.getAttribute('data-less-label') || 'Read less');

      if (!scopeAllowsCollapse()) {
        text.classList.remove('is-collapsed', 'is-expanded');
        btn.hidden = true;
        btn.onclick = null;
        return;
      }

      if (forceCollapsed) {
        ensureCollapsedButton(text, btn);
        btn.dataset.clampChecked = '0';
        scheduleClampRefresh(40);
        return;
      }

      text.classList.remove('is-collapsed');
      text.classList.add('is-expanded');
      btn.setAttribute('aria-expanded', 'true');
      btn.textContent = lessLabel;
      btn.hidden = false;
    }

    function collapseAllExpandedExcept(keepIndex) {
      if (!scopeAllowsCollapse()) return;
      slides.forEach((slide, i) => {
        if (i === keepIndex) return;
        const text = slide.querySelector('[data-grv-text]');
        const btn = slide.querySelector('[data-grv-toggle]');
        if (!text || !btn) return;
        if (text.classList.contains('is-expanded')) setCollapsedForSlide(slide, true);
      });
    }

    function initCollapse() {
      const enabled = scopeAllowsCollapse();
      let needsClampRefresh = false;

      slides.forEach((slide, i) => {
        const text = slide.querySelector('[data-grv-text]');
        const btn = slide.querySelector('[data-grv-toggle]');
        const moreLabelNode = slide.querySelector('[data-grv-more-label]');
        const moreLabel =
          (moreLabelNode && moreLabelNode.textContent.trim())
            ? moreLabelNode.textContent.trim()
            : 'Read more';
        const lessLabel = root.getAttribute('data-less-label') || 'Read less';

        if (!text || !btn) return;

        btn.dataset.moreLabel = moreLabel;
        btn.dataset.lessLabel = lessLabel;

        btn.onclick = null;
        btn.hidden = true;
        btn.setAttribute('aria-expanded', 'false');
        btn.textContent = moreLabel;

        if (!enabled) {
          text.classList.remove('is-collapsed', 'is-expanded');
          btn.hidden = true;
          return;
        }

        // default collapsed
        text.classList.remove('is-expanded');
        text.classList.add('is-collapsed');

        btn.onclick = () => {
          const expanded = btn.getAttribute('aria-expanded') === 'true';
          if (expanded) {
            setCollapsedForSlide(slide, true);
          } else {
            collapseAllExpandedExcept(i);
            setCollapsedForSlide(slide, false);
            scrollTo(i, 'smooth');
          }
        };

        if (!shouldCheckClamp(i)) return;
        btn.dataset.clampChecked = '0';
        needsClampRefresh = true;
      });

      if (needsClampRefresh) scheduleClampRefresh(60);
    }

    function recalcClampVisibilityOnly(forceAll = false) {
      if (!scopeAllowsCollapse()) return;

      slides.forEach((slide, i) => {
        const text = slide.querySelector('[data-grv-text]');
        const btn = slide.querySelector('[data-grv-toggle]');
        if (!text || !btn) return;

        if (text.classList.contains('is-expanded')) {
          btn.hidden = false;
          return;
        }

        ensureCollapsedButton(text, btn);

        if (!forceAll && !shouldCheckClamp(i) && btn.dataset.clampChecked === '1') return;
        if (!forceAll && !shouldCheckClamp(i)) return;

        setToggleForOverflow(slide, text, btn, isActuallyClamped(text));
      });
    }

    function onIndexChanged() {
      collapseAllExpandedExcept(index);
    }

    function go(i, behavior = 'smooth') {
      if (!slides.length) return;
      index = (i + slides.length) % slides.length;
      scrollTo(index, behavior);
      setActive();
      onIndexChanged();
      scheduleClampRefresh(80);
    }

    function rebuild() {
      applyLayout();
      refreshSlideMetrics();
      renderDots();

      const show = slides.length > 1;
      if (prev) prev.style.visibility = show ? 'visible' : 'hidden';
      if (next) next.style.visibility = show ? 'visible' : 'hidden';
      if (dotsWrap) dotsWrap.style.visibility = show ? 'visible' : 'hidden';

      if (index < 0 || index >= slides.length) index = getInitialIndex();
      go(index, 'auto');

      initCollapse();
      applyRelativeTimes();

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready
          .then(() => {
            scheduleClampRefresh(160);
          })
          .catch(() => {});
      }
    }

    // events
    if (prev) prev.addEventListener('click', () => go(index - 1));
    if (next) next.addEventListener('click', () => go(index + 1));

    let raf = null;
    track.addEventListener('scroll', () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!slideCenters.length) return;
        const center = track.scrollLeft + viewportWidth / 2;
        let best = 0;
        let bestDist = Infinity;

        slideCenters.forEach((c, i) => {
          const d = Math.abs(c - center);
          if (d < bestDist) {
            bestDist = d;
            best = i;
          }
        });

        if (best !== index) {
          index = best;
          setActive();
          onIndexChanged();
          scheduleClampRefresh(80);
        }
      });
    });

    window.addEventListener('resize', () => {
      clearTimeout(rebuildTimer);
      rebuildTimer = setTimeout(rebuild, 160);
    });

    if (window.ResizeObserver) {
      ro = new ResizeObserver(() => {
        clearTimeout(rebuildTimer);
        rebuildTimer = setTimeout(() => raf2(() => recalcClampVisibilityOnly(true)), 90);
      });
      ro.observe(viewport);
    }

    rebuild();

    // cleanup hook (theme editor)
    root.__grvDestroy = () => {
      try {
        if (ro) ro.disconnect();
      } catch (_) {}
      if (clampTimer) clearTimeout(clampTimer);
      root.dataset.grvInit = '0';
    };
  }

  function initWhenNearViewport(root) {
    if (!root || root.dataset.grvInit === '1' || root.dataset.grvObserve === '1') return;

    if (!('IntersectionObserver' in window)) {
      initReviews(root);
      return;
    }

    root.dataset.grvObserve = '1';
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      root.dataset.grvObserve = '0';
      initReviews(root);
    }, {
      rootMargin: '700px 0px',
      threshold: 0,
    });

    observer.observe(root);
  }

  function initAll(scope = document) {
    scope.querySelectorAll('.grv').forEach((root) => {
      if (!root.querySelector('[data-grv-track]')) return;
      if (window.Shopify && window.Shopify.designMode) {
        initReviews(root);
      } else {
        initWhenNearViewport(root);
      }
    });
  }

  // initial
  document.addEventListener('DOMContentLoaded', () => initAll());

  // Theme editor: section reload / reorder
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:reorder', () => initAll());
  document.addEventListener('shopify:section:select', (e) => initAll(e.target));
})();
